import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
    Background,
    Controls,
    Handle,
    Position,
    addEdge,
    reconnectEdge,
    applyEdgeChanges,
    applyNodeChanges,
    ConnectionLineType
} from 'reactflow';
import 'reactflow/dist/style.css';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';

const COLORS_12 = [
    '#FFFFFF', '#8B4513', '#00FF00', '#FFFF00',
    '#808080', '#FFC0CB', '#0000FF', '#FF0000',
    '#FFA500', '#800080', '#000000', '#EE82EE'
];
const COLORS_5 = ['#8B4513', '#FFFFFF', '#0000FF', '#000000', '#808080'];

const getM12PinLabels = (name, pinCount) => {
    const cleanName = name.toUpperCase();
    let labels = Array.from({ length: pinCount }, (_, i) => `Pin ${i + 1}`);

    // Helper to set specific index (1-based to 0-based)
    const setPin = (p, text) => { if (p <= pinCount) labels[p - 1] = text; };

    if (cleanName.includes('CAN') && cleanName.includes('24VDC')) {
        setPin(1, 'Shield');
        setPin(2, '+V');
        setPin(3, '0V');
        setPin(4, 'High');
        setPin(5, 'Low');
    }
    else if (cleanName.includes('M12-5M 24VDC')) {
        labels = labels.map(() => 'I/O');
        setPin(1, '+V');
        setPin(3, '0V');
    }
    else if (cleanName.includes('SAFIX') && !cleanName.includes('2 X')) {
        setPin(1, '+V');
        setPin(2, '+V');
        setPin(3, '0V');
        setPin(4, 'OSSD');
        setPin(5, 'Info');
        setPin(6, '+V');
        setPin(7, 'OSSD');
        setPin(8, '+V');
    }
    else if (cleanName.includes('2 X SAFIX')) {
        setPin(1, '+V');
        setPin(2, '+V');
        setPin(3, '0V');
        setPin(4, 'OSSD');
        setPin(5, 'Info');
        setPin(6, 'Info');
        setPin(7, 'OSSD');
        setPin(8, '+V');
    }
    else if (cleanName.includes('HOLDX') && !cleanName.includes('2 X')) {
        setPin(1, '+V');
        setPin(2, '+V');
        setPin(3, '0V');
        setPin(4, 'OSSD');
        setPin(5, 'Info');
        setPin(6, '+V');
        setPin(7, 'OSSD');
        setPin(8, 'Magnet Lock');
    }
    else if (cleanName.includes('2 X HOLDX')) {
        setPin(1, '+V');
        setPin(2, 'Magnet Lock 1');
        setPin(3, '0V');
        setPin(4, 'OSSD 1 & 2');
        setPin(5, 'Info');
        setPin(6, 'Info');
        setPin(7, 'OSSD 1 & 2');
        setPin(8, 'Magnet Lock 2');
    }
    else if (cleanName.includes('IDEM')) {
        setPin(1, 'OSSD');
        setPin(2, '+V');
        setPin(3, '0V');
        setPin(4, '+V');
        setPin(5, 'Not Used');
        setPin(6, 'OSSD');
        setPin(7, '+V');
        setPin(8, 'Info');
    }
    else if (cleanName.includes('EDEN')) {
        setPin(1, 'OSSD 1 Out');
        setPin(2, '+V');
        setPin(3, 'OSSD 1 In');
        setPin(4, 'OSSD 2 In');
        setPin(5, 'Reset/Info');
        setPin(6, 'OSSD 2 Out');
        setPin(7, '0V');
        setPin(8, 'Info');
    }

    return labels;
};

const FrontPanelNode = ({ data }) => {
    const { pins, side = 'right' } = data;

    return (
        <div style={{ background: '#ffd700', border: '2px solid #002846', borderRadius: '8px', width: '180px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <div style={{ background: '#002846', padding: '6px', fontSize: '11px', fontWeight: 900, textAlign: 'center', color: '#ffd700', borderRadius: '6px 6px 0 0' }}>FRONT PANEL COMPONENTS</div>
            <div style={{ padding: '8px 4px' }}>
                {pins.map((pinLabel, idx) => (
                    <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '24px',
                        fontSize: '10px',
                        fontWeight: 700,
                        justifyContent: side === 'left' ? 'flex-end' : 'flex-start',
                        position: 'relative'
                    }}>
                        {side === 'left' && <span style={{ marginRight: '10px', textAlign: 'right', color: '#002846' }}>{pinLabel}</span>}

                        <Handle
                            type="target"
                            position={side === 'left' ? Position.Right : Position.Left}
                            id={`FP-pin-${pinLabel.replace(/\s+/g, '-')}`}
                            style={{ background: '#002846', width: '8px', height: '8px' }}
                        />
                        {side === 'right' && <span style={{ marginLeft: '10px', textAlign: 'left', color: '#002846' }}>{pinLabel}</span>}
                    </div>
                ))}
            </div>
        </div>
    );
};

// Node junctions (Splice)
const JunctionNode = ({ data }) => {
    const { label, side } = data;
    return (
        <div style={{ width: '15px', height: '15px', background: '#002846', borderRadius: '50%', border: '2px solid #ffd700', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', color: 'white', fontWeight: 900 }}>
            <div style={{ position: 'absolute', top: -15, color: '#002846', whiteSpace: 'nowrap' }}>S{label}</div>
            <Handle type="target" position={side === 'left' ? Position.Right : Position.Left} style={{ opacity: 0 }} />
            <Handle type="source" position={side === 'left' ? Position.Left : Position.Right} style={{ opacity: 0 }} />
        </div>
    );
};

// M12 Node with specific labels
const M12Node = ({ data }) => {
    const { label, pins, side } = data;
    return (
        <div style={{ background: '#ffd700', border: '2px solid #ffd700', borderRadius: '8px', width: '160px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <div style={{ background: '#ffd700', padding: '6px', fontSize: '11px', fontWeight: 900, textAlign: 'center', color: '#002846', borderBottom: '1px solid #eab308' }}>{label}</div>
            <div style={{ padding: '8px 4px' }}>
                {pins.map((pinLabel, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', height: '24px', fontSize: '10px', fontWeight: 700, justifyContent: side === 'left' ? 'flex-end' : 'flex-start', position: 'relative' }}>
                        {side === 'left' && <span style={{ marginRight: '10px', textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>{pinLabel}</span>}
                        <Handle
                            type="target"
                            position={side === 'left' ? Position.Right : Position.Left}
                            id={`${label.split(':')[0]}-pin-${i + 1}`}
                            style={{ background: '#002846', width: '8px', height: '8px' }}
                        />
                        {side === 'right' && <span style={{ marginLeft: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>{pinLabel}</span>}
                    </div>
                ))}
            </div>
        </div>
    );
};

// CAN Node
const CanNode = ({ data }) => {
    const { pins, label, side = 'right' } = data;

    return (
        <div style={{ background: '#e0f2fe', border: '2px solid #002846', borderRadius: '8px', width: '140px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <div style={{ background: '#002846', padding: '6px', fontSize: '11px', fontWeight: 900, textAlign: 'center', color: '#e0f2fe' }}>{label}</div>
            <div style={{ padding: '8px 4px' }}>
                {pins.map((pinLabel, i) => (
                    <div key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '24px',
                        fontSize: '10px',
                        fontWeight: 700,
                        justifyContent: side === 'left' ? 'flex-end' : 'flex-start',
                        position: 'relative'
                    }}>
                        {side === 'left' && <span style={{ marginRight: '10px', textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>{pinLabel}</span>}

                        <Handle
                            type="source"
                            position={side === 'left' ? Position.Right : Position.Left}
                            id={`CAN-pin-${i + 1}`}
                            style={{ background: '#0369a1', width: '8px', height: '8px' }}
                        />
                        {side === 'right' && <span style={{ marginLeft: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>{pinLabel}</span>}
                    </div>
                ))}
            </div>
        </div>
    );
};

// Node I/O
const MainPLCNode = ({ data }) => {
    const { hasRelays } = data;
    const leftBase = ['0V', '0V', '0V', '+V', '0V', '1', '3', '5', '7', '9', '11', '13'];
    const leftRelays = ['23', '24', '33', '34'];
    const rightBase = ['+V', '+V', '+V', '+V', '0V', '2', '4', '6', '8', '10', '12', '14'];
    const rightRelays = ['43', '44', '53', '54'];
    const leftPins = hasRelays ? [...leftBase, ...leftRelays] : leftBase;
    const rightPins = hasRelays ? [...rightBase, ...rightRelays] : rightBase;

    const renderPin = (label, side, disabled = false, isLast = false, index) => (
        <div key={`${side}-${label}-${index}`} style={{ display: 'flex', alignItems: 'center', height: '24px', padding: '0 8px', justifyContent: side === 'left' ? 'flex-start' : 'flex-end', background: disabled ? '#f8fafc' : 'transparent', color: disabled ? '#ffffff' : '#1e293b', fontSize: '9px', fontWeight: 700, position: 'relative' }}>
            {side === 'right' && <span style={{ marginRight: '8px' }}>{label}</span>}
            <Handle type="source" position={side === 'left' ? Position.Left : Position.Right} id={`I/O-${side}-${label}-${index}`} style={{ background: disabled ? '#cbd5e1' : '#002846', width: '7px', height: '7px' }} />
            {side === 'left' && <span style={{ marginLeft: '8px' }}>{label}</span>}
            {!isLast && <div style={{ position: 'absolute', bottom: 0, width: '80%', height: '1px', background: '#ffffff', left: '50%', transform: 'translateX(-50%)' }} />}
        </div>
    );

    return (
        <div style={{ background: '#ffd700', border: '2px solid #002846', borderRadius: '8px', width: '210px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#ffd700', borderRadius: '8px 8px 0 0', padding: '10px 5px', textAlign: 'center', fontWeight: 900, fontSize: '12px', color: '#002846', borderBottom: '1px solid rgba(0,40,70,0.1)' }}>I/O TERMINAL BLOCK</div>
            {hasRelays && (
                <svg style={{ position: 'absolute', top: 38, left: 0, width: '100%', height: 'calc(100% - 38px)', pointerEvents: 'none', zIndex: 0 }}>
                    {/* Relay 15 */}
                    <g fill="none" stroke="#002846" strokeWidth="1.2">
                        {/* Horizontal lines */}
                        <path d="M 35,300 L 45,300" /> {/* Terminal 23 */}
                        <path d="M 35,324 L 45,324" /> {/* Terminal 24 */}
                        <path d="M 35,348 L 45,348" /> {/* Terminal 33 */}
                        <path d="M 35,372 L 45,372" /> {/* Terminal 34 */}

                        {/* Nubs and swinging arm for 23-24 */}
                        <line x1="45" y1="300" x2="45" y2="305" strokeWidth="1.4" /> {/* Top nub */}
                        <line x1="45" y1="324" x2="45" y2="319" strokeWidth="1.4" /> {/* Bottom nub */}
                        <line x1="45" y1="319" x2="53" y2="305" strokeWidth="1.4" /> {/* Swinging arm */}

                        {/* Nubs and swinging arm for 33-34 */}
                        <line x1="45" y1="348" x2="45" y2="353" strokeWidth="1.4" /> {/* Top nub */}
                        <line x1="45" y1="372" x2="45" y2="367" strokeWidth="1.4" /> {/* Bottom nub */}
                        <line x1="45" y1="367" x2="53" y2="353" strokeWidth="1.4" /> {/* Swinging arm */}

                        {/* Dashed lines */}
                        <path d="M 55,312 L 65,312 L 65,360 L 55,360" strokeDasharray="4,2" strokeWidth="1.0" />
                        <line x1="65" y1="336" x2="70" y2="336" strokeDasharray="4,2" strokeWidth="1.0" />
                    </g>
                    <text x="73" y="340" fill="#002846" fontSize="12px" fontWeight="500">15</text>

                    {/* Relay 16 */}
                    <g fill="none" stroke="#002846" strokeWidth="1.2">
                        {/* Horizontal lines */}
                        <path d="M 175,300 L 165,300" /> {/* Terminal 43 */}
                        <path d="M 175,324 L 165,324" /> {/* Terminal 44 */}
                        <path d="M 175,348 L 165,348" /> {/* Terminal 53 */}
                        <path d="M 175,372 L 165,372" /> {/* Terminal 54 */}

                        {/* Nubs and swinging arm for 43-44 */}
                        <line x1="165" y1="300" x2="165" y2="305" strokeWidth="1.4" /> {/* Top nub */}
                        <line x1="165" y1="324" x2="165" y2="319" strokeWidth="1.4" /> {/* Bottom nub */}
                        <line x1="165" y1="319" x2="156" y2="304" strokeWidth="1.4" /> {/* Swinging arm */}

                        {/* Nubs and swinging arm for 53-54 */}
                        <line x1="165" y1="348" x2="165" y2="353" strokeWidth="1.4" /> {/* Top nub */}
                        <line x1="165" y1="372" x2="165" y2="367" strokeWidth="1.4" /> {/* Bottom nub */}
                        <line x1="165" y1="367" x2="156" y2="352" strokeWidth="1.4" /> {/* Swinging arm */}

                        {/* Dashed lines */}
                        <path d="M 155,312 L 145,312 L 145,360 L 155,360" strokeDasharray="4,2" strokeWidth="1.0" />
                        <line x1="145" y1="336" x2="140" y2="336" strokeDasharray="4,2" strokeWidth="1.0" />
                    </g>
                    <text x="123" y="340" fill="#002846" fontSize="12px" fontWeight="500">16</text>
                </svg>
            )}
            <div style={{ display: 'flex', flex: 1 }}>
                <div style={{ flex: 1, borderRight: '1px solid rgba(0,40,70,0.1)' }}>
                    {leftPins.map((p, i) => renderPin(p, 'left', false, i >= leftPins.length - (hasRelays ? 4 : 1), i))}
                </div>
                <div style={{ flex: 1 }}>
                    {rightPins.map((p, i) => renderPin(p, 'right', false, i >= rightPins.length - (hasRelays ? 4 : 1), i))}
                </div>
            </div>
        </div>
    );
};

const IODesigner = ({ config, isOpen, onClose, edges, setEdges, nodes, setNodes, onSaveImage }) => {
    const [selectedEdge, setSelectedEdge] = useState(null);
    const edgeReconnectSuccessful = useRef(true);
    const hasRelays = useMemo(() => config?.inner_components?.some(i => i.variant === 'relay'), [config]);
    const onNodesChange = useCallback((changes) => setNodes((nds) => applyNodeChanges(changes, nds)), [setNodes]);
    const onEdgesChange = useCallback((changes) => setEdges((eds) => applyEdgeChanges(changes, eds)), [setEdges]);

    useEffect(() => {
        setEdges((eds) => eds.filter((edge) =>
            nodes.find((n) => n.id === edge.source) &&
            nodes.find((n) => n.id === edge.target)
        ));
    }, [nodes, setEdges, setNodes]);

    const onNodeDrag = useCallback((_, node) => {
        if (node.type !== 'm12Connector' &&
            node.type !== 'junction' &&
            node.type !== 'canNode' &&
            node.type !== 'frontPanel') return;

        // I/O is at x=350 with width=210, the center is roughly 455
        // If Node X < 455, it's on the left side, pins face Right)
        // If Node X > 455, it's on the right side, pins face Left)
        const centerThreshold = 455;
        const newSide = node.position.x < centerThreshold ? 'left' : 'right';

        if (node.data.side !== newSide) {
            setNodes((nds) =>
                nds.map((n) => {
                    if (n.id === node.id) {
                        return {
                            ...n,
                            data: { ...n.data, side: newSide }
                        };
                    }
                    return n;
                })
            );
        }
    }, [setNodes]);

    const determineColor = useCallback((params) => {
        const getPinData = (nodeId, handleId) => {
            const node = nodes.find(n => n.id === nodeId);
            if (node?.type === 'm12Connector') {
                const pinNum = parseInt(handleId?.split('-pin-')[1]);
                return { pin: pinNum, count: node.data.pinCount };
            }
            return null;
        };
        const data = getPinData(params.source, params.sourceHandle) || getPinData(params.target, params.targetHandle);
        if (!data) return '#ffd700';
        return data.count === 5 ? (COLORS_5[data.pin - 1] || '#808080') : (COLORS_12[data.pin - 1] || '#ffd700');
    }, [nodes]);

    useEffect(() => {
        if (!hasRelays && edges.length > 0) {
            setEdges((eds) => eds.filter((edge) => {
                const relayPins = ['23', '24', '33', '34', '43', '44', '53', '54'];
                return !relayPins.some(
                    pin => edge.sourceHandle?.includes(`-${pin}-`) || edge.targetHandle?.includes(`-${pin}-`)
                );
            }));
        }
    }, [hasRelays, edges.length, setEdges]);

    const addJunction = () => {
        const junctionCount = nodes.filter(n => n.type === 'junction').length + 1;
        const id = `junction-${junctionCount}-${Date.now()}`;
        setNodes((nds) => [
            ...nds,
            {
                id,
                type: 'junction',
                position: { x: 280, y: 150 + (junctionCount * 30) },
                data: { label: junctionCount }
            }
        ]);
    };

    const onConnect = useCallback((params) => {
        const color = determineColor(params);
        setEdges((eds) => addEdge({
            ...params,
            type: 'bezier',
            animated: true,
            style: { strokeWidth: 4, stroke: color }, data: { color }
        }, eds));
    }, [determineColor, setEdges]);

    const onEdgeClick = useCallback((_, edge) => {
        if (!edge) return;
        setSelectedEdge(edge.id);
        setEdges((eds) => eds.map((e) => ({
            ...e,
            style: {
                ...e.style,
                strokeWidth: e.id === edge.id ? 6 : 4,
                stroke: e.id === edge.id ? '#ff0000' : (e.data?.color || '#ffd700'),
                zIndex: e.id === edge.id ? 1000 : 1
            }
        })));
    }, [setEdges]);

    const onPaneClick = useCallback(() => {
        setSelectedEdge(null);
        setEdges((eds) => eds.map((e) => ({
            ...e,
            style: { ...e.style, strokeWidth: 4, stroke: e.data?.color || '#ffd700' }
        })));
    }, [setEdges]);

    const onReconnectStart = useCallback(() => { edgeReconnectSuccessful.current = false; }, []);
    const onReconnect = useCallback((oldEdge, newConnection) => {
        edgeReconnectSuccessful.current = true;
        setEdges((els) => reconnectEdge(oldEdge, newConnection, els));
    }, [setEdges]);

    const onReconnectEnd = useCallback((_, edge) => {
        if (!edgeReconnectSuccessful.current) setEdges((eds) => eds.filter((e) => e.id !== edge.id));
        edgeReconnectSuccessful.current = true;
    }, [setEdges]);

    const clearWiring = () => { if (window.confirm("Delete all wires?")) { setEdges([]); setSelectedEdge(null); } };

    useEffect(() => {
        if (!config || !isOpen) return;

        const freshNodes = [{
            id: 'main-plc',
            type: 'mainPLC',
            position: { x: 350, y: 50 },
            data: { hasRelays },
            zIndex: 1,
            draggable: false
        }];

        const canComponent = config.inner_components?.find(c => c.variant === 'can_single' || c.variant === 'can_double');
        if (canComponent) {
            const isSingle = canComponent.variant === 'can_single';
            freshNodes.push({
                id: 'internal-can',
                type: 'canNode',
                position: { x: 600, y: 550 },
                data: {
                    pins: isSingle ? ['High', 'Low', 'Shield'] : ['High 1', 'Low 1', 'Shield 1', 'High 2', 'Low 2', 'Shield 2'],
                    label: isSingle ? 'CAN INTERFACE' : 'CAN INTERFACE'
                }
            });
        }

        const sidePlacements = {
            Q1: { x: 80, y: 500, side: 'left' },
            Q2: { x: 80, y: 50, side: 'left' },
            Q3: { x: 780, y: 500, side: 'right' },
            Q4: { x: 780, y: 50, side: 'right' }
        };

        Object.entries(sidePlacements).forEach(([qKey, pos]) => {
            const connector = config[qKey];
            if (!connector || ['yellow_plug', 'grey_plug', 'cable_gland', 'none'].includes(connector.variant)) return;

            const name = connector.name || '';
            const pinCount = name.includes('-12') ? 12 : name.includes('-8') ? 8 : 5;
            freshNodes.push({
                id: qKey,
                type: 'm12Connector',
                position: { x: pos.x, y: pos.y },
                data: {
                    label: `${qKey}: ${name}`,
                    pins: getM12PinLabels(name, pinCount),
                    pinCount,
                    side: pos.side
                }
            });
        });

        const fpPins = ['V+', '0V'];
        let housingActive = false;
        ['Q5', 'Q6', 'Q7', 'Q8', 'Q9'].forEach((q) => {
            const comp = config[q];
            if (!comp || ['none', 'yellow_plug', 'grey_plug', 'cable_gland', 'plug_button'].includes(comp.variant)) return;

            if (comp.variant.includes('m12')) {
                const pinCount = comp.name.includes('-12') ? 12 : comp.name.includes('-8') ? 8 : 5;
                freshNodes.push({
                    id: q,
                    type: 'm12Connector',
                    position: { x: q === 'Q5' || q === 'Q6' ? 80 : 780, y: 275 },
                    data: {
                        label: `${q}: ${comp.name}`,
                        pins: getM12PinLabels(comp.name, pinCount),
                        pinCount,
                        side: q === 'Q5' || q === 'Q6' ? 'left' : 'right'
                    }
                });
            } else {
                housingActive = true;
                const n = comp.name || '';
                if (comp.variant.includes('e_stop_led')) fpPins.push(`${q}: NC 1 (${n})`, `${q}: NC 2 (${n})`, `${q}: LED (${n})`);
                else if (comp.variant.includes('e_stop')) fpPins.push(`${q}: NC 1 (${n})`, `${q}: NC 2 (${n})`);
                else if (comp.variant.includes('switch')) fpPins.push(`${q}: NO (${n})`, `${q}: NC (${n})`, ...(q !== 'Q5' && q !== 'Q8' ? [`${q}: V+ (${n})`] : []));
                else if (comp.variant.includes('button')) fpPins.push(`${q}: Push Button (${n})`);
            }
        });

        if (housingActive) {
            freshNodes.push({
                id: 'front-panel-group',
                type: 'frontPanel',
                position: { x: 600, y: 50 },
                data: { pins: fpPins }
            });
        }

        //  Preserves positions if the node already exists
        setNodes((prevNodes) => {
            const merged = freshNodes.map(newNode => {
                const existing = prevNodes.find(n => n.id === newNode.id);
                if (existing) {
                    return {
                        ...newNode,
                        position: existing.position,
                        data: { ...newNode.data, ...existing.data } // Side data
                    };
                }
                return newNode;
            });

            // Keep junctions which are not in config
            const junctions = prevNodes.filter(n => n.type === 'junction');

            return [...merged, ...junctions];
        });

    }, [config, hasRelays, isOpen, setNodes]);

    const getHandleLabel = (nodeId, handleId) => {
        if (handleId && handleId.startsWith('I/O-')) {
            return handleId.substring(0, handleId.lastIndexOf('-'));
        }
        if (nodeId.startsWith('junction')) return `Splice ${nodeId.split('-')[1]}`;
        return handleId || 'Unknown';
    };

    // Ref for the wrapper div that holds the ReactFlow diagram
    const flowRef = useRef(null);
    const reactFlowInstance = useRef(null);

    const downloadPDF = useCallback(() => {
        if (!flowRef.current) return;

        toPng(flowRef.current, { backgroundColor: '#334155', pixelRatio: 2 })
            .then((dataUrl) => {
                const tempPdf = new jsPDF();
                const imgProps = tempPdf.getImageProperties(dataUrl);
                const pdfWidth = imgProps.width;
                const pdfHeight = imgProps.height;

                const pdf = new jsPDF({
                    orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
                    unit: 'px',
                    format: [pdfWidth, pdfHeight]
                });

                pdf.addImage(dataUrl, 'PNG', 0, 0, pdfWidth, pdfHeight);
                pdf.save('wiring-diagram.pdf');
            })
            .catch((err) => {
                console.error('PDF export failed:', err);
            });
    }, []);

    // Capture image before closing
    const handleCloseAndSave = useCallback(() => {
        // Force the canvas to center and fit all nodes instantly
        if (reactFlowInstance.current) {
            reactFlowInstance.current.fitView({ padding: 0.1, duration: 0 });
        }

        // Wait 100ms for the canvas to visually update before snapping the picture
        setTimeout(() => {
            if (flowRef.current && edges.length > 0) {
                toPng(flowRef.current, { backgroundColor: '#334155', pixelRatio: 2 })
                    .then((dataUrl) => {
                        onSaveImage(dataUrl); // Send perfectly centered image to App.js
                        onClose();            // Close the modal
                    })
                    .catch((err) => {
                        console.error('Failed to capture wiring diagram:', err);
                        onClose();
                    });
            } else {
                // If empty or ref missing, clear image and close
                onSaveImage(null);
                onClose();
            }
        }, 100);
    }, [edges.length, onClose, onSaveImage]);

    if (!isOpen) return null;

    return (
        <div className="io-designer-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'crosshair' }}>
            <div style={{ background: '#f8fafc', width: '98vw', height: '94vh', borderRadius: '12px', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
                <div style={{ background: '#002846', color: 'white', padding: '15px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 900 }}>WIRING DESIGNER</h2>
                        <div style={{ fontSize: '12px', background: 'rgba(255,255,255,0.1)', padding: '4px 10px', borderRadius: '4px', fontWeight: 700 }}>{edges.length} WIRE{edges.length !== 1 ? 'S' : ''}</div>
                        <button onClick={addJunction} style={{ background: '#10b981', border: 'none', color: 'white', padding: '6px 12px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>+ ADD SPLICE</button>
                        <button onClick={clearWiring} style={{ background: '#ef4444', border: 'none', color: 'white', padding: '6px 12px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>CLEAR ALL</button>
                        <button onClick={downloadPDF} style={{ background: '#3b82f6', border: 'none', color: 'white', padding: '6px 12px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                            ⬇ PDF
                        </button>
                    </div>
                    <button onClick={handleCloseAndSave} style={{ background: '#ffd700', border: 'none', padding: '10px 25px', borderRadius: '6px', fontWeight: 900, cursor: 'pointer', color: '#002846' }}>CLOSE</button>
                </div>
                <div ref={flowRef} style={{ flex: 1, position: 'relative' }}>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onInit={(instance) => { reactFlowInstance.current = instance; }}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onEdgeClick={onEdgeClick}
                        onPaneClick={onPaneClick}
                        onReconnect={onReconnect}
                        onReconnectStart={onReconnectStart}
                        onReconnectEnd={onReconnectEnd}
                        onNodeDrag={onNodeDrag}
                        nodeTypes={{
                            mainPLC: MainPLCNode,
                            m12Connector: M12Node,
                            junction: JunctionNode,
                            frontPanel: FrontPanelNode,
                            canNode: CanNode
                        }}
                        connectionLineType={ConnectionLineType.Bezier}
                        connectionRadius={40}
                        connectionLineStyle={{ stroke: '#ffd700', strokeWidth: 4 }} snapToGrid={true} snapGrid={[20, 20]} fitView style={{ width: '100%', height: '100%', backgroundColor: 'rgba(4, 161, 201, 1)' }}
                    >
                        <Background color="#334155" gap={20} />
                        <Controls />
                    </ReactFlow>

                    {edges.length > 0 && (
                        <div className="connection-list-overlay" style={{ position: 'absolute', top: '20px', right: '20px', background: 'white', padding: '15px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxHeight: '70vh', overflowY: 'auto', zIndex: 10, minWidth: '250px' }}>
                            <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 900, color: '#002846' }}>CONNECTIONS ({edges.length})</h3>
                            {edges.map((edge, idx) => (
                                <div key={edge.id} onClick={() => onEdgeClick(null, edge)} style={{ padding: '8px', background: selectedEdge === edge.id ? '#fffbeb' : '#f8fafc', marginBottom: '6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, borderLeft: `4px solid ${edge.data?.color || '#ffd700'}`, cursor: 'pointer', transition: 'all 0.2s' }}>
                                    <div style={{ fontWeight: 700, color: '#002846' }}>Wire {idx + 1}</div>
                                    <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>
                                        {getHandleLabel(edge.source, edge.sourceHandle)} → {getHandleLabel(edge.target, edge.targetHandle)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IODesigner;